import produce from "immer";
import { CustomizationState, InitialCustomizationState } from "../";
import { Configuration } from "../../../types/customization/configuration";
import { ContentPage } from "../../../types/customization/content";
import { TemplatePageDefinition } from "../../../types/customization/template";
import { SelectionPhoto } from "../../../types/selection/selection";
import { getNotEmptyPages } from "../../../utils/customization/content";
import { getCoverFilteredPages, getNoCoverDefinitions } from "../../../utils/customization/cover";
import {
  findPageDefinition,
  findPageDefinitionsWithValidCondition,
} from "../../../utils/customization/finders";
import {
  countEmptyEditablePictures,
  getNumberPagesToAdd,
  isEmptyPage,
} from "../../../utils/customization/page";
import { changeOnePagePageDefinition } from "../../../utils/customization/pageDefinition";
import {
  countEditablePicturesInPage,
  findBestLayoutByOrientation,
  layoutsByEditablePicturesCount,
} from "../../../utils/customization/template";
import { TemplatePageDefinitionsByCountKey } from "../../../utils/customization/template";
import { doAddPage } from "./addPage";
import { doAddPicture } from "./addPicture";

export interface AddPicturesWithAutofillAction {
  configuration: Configuration | undefined;
  selectedPictures: SelectionPhoto[];
  type: "addPicturesWithAutofill";
}
interface LayoutsByCountKey {
  [key: string]: TemplatePageDefinitionsByCountKey | null;
}

export function addPicturesWithAutofill(
  state: CustomizationState,
  action: AddPicturesWithAutofillAction
): CustomizationState {
  return produce(state, (draftState) => {
    const { configuration, selectedPictures } = action;
    const { content, template } = draftState;
    const isPrint = template.templateType === "print";
    const productOptions = configuration?.options || [];

    const minPages = configuration && configuration.pages && configuration.pages.min;
    const maxPages = configuration && configuration.pages && configuration.pages.max;

    const conditionFilteredPageDefinitions = findPageDefinitionsWithValidCondition(
      template,
      content,
      {},
      configuration?.options
    );
    const noCoverPageDefinitions = conditionFilteredPageDefinitions
      ? getNoCoverDefinitions(conditionFilteredPageDefinitions)
      : [];
    let pagesWithoutCover = getCoverFilteredPages(content.pages, noCoverPageDefinitions);

    const nbMinPages = minPages ? minPages : 1;
    const nbMaxPages = maxPages ? maxPages : isPrint ? 1000 : 0;

    const lastPageDefinitionKey =
      pagesWithoutCover.length > 0
        ? pagesWithoutCover[pagesWithoutCover.length - 1].pageDefinitionKey
        : noCoverPageDefinitions[0].key;
    const layoutsByPageDefinitionKey: LayoutsByCountKey = {};
    for (const noCoverPageDefinition of noCoverPageDefinitions) {
      const layouts = layoutsByEditablePicturesCount(
        noCoverPageDefinition.key,
        noCoverPageDefinitions,
        true,
        false,
        false
      );
      layoutsByPageDefinitionKey[noCoverPageDefinition.key] = layouts;
    }

    const minPagesNotEmpty = getNotEmptyPages(pagesWithoutCover.slice(0, nbMinPages));
    const additionalPagesNotEmpty = getNotEmptyPages(pagesWithoutCover.slice(nbMinPages));
    const availableSlotsOnNotEmptyPages = countEmptyEditablePictures(
      [...minPagesNotEmpty, ...additionalPagesNotEmpty],
      template
    );
    const densities = Object.keys(layoutsByPageDefinitionKey[lastPageDefinitionKey] || {}).map(
      (numKey) => +numKey
    );
    const pageDensitiesConfig = getPagesDensities({
      minPageCount: nbMinPages,
      maxPageCount: nbMaxPages,
      minPagesNotEmptyCount: minPagesNotEmpty.length,
      additionalPagesNotEmptyCount: additionalPagesNotEmpty.length,
      availableSlotsOnNotEmptyPages,
      photoToAddCount: selectedPictures.length,
      densities,
      print: isPrint,
    });

    if (pageDensitiesConfig && pageDensitiesConfig.minPagesToHave > 0) {
      // add missing pages with densityForMinPages
      addRequiredPages(
        pageDensitiesConfig.minPagesToHave,
        pageDensitiesConfig.densityForMinPages,
        pagesWithoutCover,
        lastPageDefinitionKey,
        layoutsByPageDefinitionKey,
        draftState,
        configuration
      );
    }

    // we need to update pagesWithoutCover content
    pagesWithoutCover = getCoverFilteredPages(content.pages, noCoverPageDefinitions);
    if (pageDensitiesConfig && pageDensitiesConfig.additionnalPagesCount > 0) {
      // add missing pages with maxDensityOnAdditionnalPages
      const additionnalPages = pagesWithoutCover.slice(minPages);
      addRequiredPages(
        pageDensitiesConfig.additionnalPagesCount,
        pageDensitiesConfig.maxDensityOnAdditionnalPages,
        additionnalPages,
        lastPageDefinitionKey,
        layoutsByPageDefinitionKey,
        draftState,
        configuration
      );
    }

    pagesWithoutCover = getCoverFilteredPages(content.pages, noCoverPageDefinitions);
    let selectionPhotoPlaced = 0;
    for (const page of pagesWithoutCover) {
      const pageDefinition = findPageDefinition(page.pageDefinitionKey, template);
      const nbPhotosFilledInPage = page.editablePictures.length;
      const nbSlots = countEditablePicturesInPage(pageDefinition);
      const nbEmptySlots = nbSlots - nbPhotosFilledInPage;

      if (isEmptyPage(page) && selectionPhotoPlaced < selectedPictures.length) {
        let newPageDefinition: TemplatePageDefinition | null = null;
        if (layoutsByPageDefinitionKey !== null) {
          const layoutsForPage = layoutsByPageDefinitionKey[page.pageDefinitionKey];
          const layoutsOfSameDensity = layoutsForPage && layoutsForPage[nbSlots];
          newPageDefinition =
            layoutsOfSameDensity && layoutsOfSameDensity.length > 1 && layoutsForPage
              ? findBestLayoutByOrientation(
                  layoutsOfSameDensity,
                  selectedPictures.slice(selectionPhotoPlaced, selectionPhotoPlaced + nbEmptySlots)
                )
              : null;
        }
        if (newPageDefinition) {
          changeOnePagePageDefinition(page, draftState, newPageDefinition.key, productOptions);
        }
      }

      for (let i = 0; i < nbEmptySlots; i++) {
        if (!selectedPictures[selectionPhotoPlaced]) {
          return;
        }
        doAddPicture(draftState, {
          avoidCover: true,
          configuration,
          selectionPhoto: selectedPictures[selectionPhotoPlaced],
          tryToFillFirstEmptyPicture: true,
          type: "addPicture",
        });

        selectionPhotoPlaced++;
      }
    }
  });
}

function getKeysByOrderAsc(
  layoutsByCount: TemplatePageDefinitionsByCountKey | null
): string[] | null {
  return layoutsByCount ? Object.keys(layoutsByCount).sort((a, b) => +a - +b) : null;
}

/* The auto fill should try to use second layout density max until max page is reached.
// Once max page is reached, it should choose higher density until max selected pictures are used */
export function getPagesDensities({
  minPageCount,
  maxPageCount,
  minPagesNotEmptyCount,
  additionalPagesNotEmptyCount,
  availableSlotsOnNotEmptyPages,
  photoToAddCount,
  densities,
  print,
}: {
  minPageCount: number;
  maxPageCount: number;
  minPagesNotEmptyCount: number;
  additionalPagesNotEmptyCount: number;
  availableSlotsOnNotEmptyPages: number;
  photoToAddCount: number;
  densities: number[];
  print: boolean;
}): {
  minPagesToHave: number;
  densityForMinPages: number;
  additionnalPagesCount: number;
  maxDensityOnAdditionnalPages: number;
} | null {
  const minPageAvailableCount = Math.max(minPageCount - minPagesNotEmptyCount, 0);
  const maxPageAvailableCount = Math.max(
    maxPageCount - minPagesNotEmptyCount - additionalPagesNotEmptyCount,
    0
  );
  const photosToPlaceCount = photoToAddCount - availableSlotsOnNotEmptyPages;

  if (maxPageAvailableCount === 0 || photosToPlaceCount < 1 || densities.length === 0) {
    // should fill only empty slots
    return null;
  }

  // prints will use same density on all pages
  if (print || densities.length === 1) {
    let maxDensity = densities[0];
    for (
      let index = 0;
      photosToPlaceCount > maxPageAvailableCount * densities[index] && index < densities.length - 1;
      index++
    ) {
      maxDensity = densities[index + 1];
    }

    const totalPagesToFill = Math.min(
      Math.ceil(photosToPlaceCount / maxDensity),
      maxPageAvailableCount
    );
    const totalPagesToHave = totalPagesToFill + minPagesNotEmptyCount;
    const minPagesToHave = totalPagesToHave > minPageCount ? minPageCount : totalPagesToHave;
    const remainingPagesToCreate = totalPagesToHave - minPagesToHave;

    return {
      minPagesToHave,
      densityForMinPages: maxDensity,
      additionnalPagesCount: remainingPagesToCreate > 0 ? remainingPagesToCreate : 0,
      maxDensityOnAdditionnalPages: remainingPagesToCreate > 0 ? maxDensity : 0,
    };
  }

  const photosOnMaxPageCount = Math.ceil(photosToPlaceCount / maxPageAvailableCount);
  const maxAdditionalPageCount = maxPageAvailableCount - minPageAvailableCount;

  if (photosOnMaxPageCount > densities[1]) {
    let maxDensity = densities[1];
    for (
      let index = 1;
      photosOnMaxPageCount > densities[index] && index < densities.length - 1;
      index++
    ) {
      maxDensity = densities[index + 1];
    }
    const additionnalPagesCount = Math.max(
      Math.ceil(photosToPlaceCount / maxDensity) - minPageAvailableCount,
      0
    );

    return {
      minPagesToHave: minPageCount,
      densityForMinPages: maxDensity,
      additionnalPagesCount: Math.min(additionnalPagesCount, maxAdditionalPageCount),
      maxDensityOnAdditionnalPages: additionnalPagesCount > 0 ? maxDensity : 0,
    };
  } else {
    let densityOnMinPage;
    let densityOnAdditionnalPages = 0;
    let additionnalPagesCount = 0;

    const density =
      minPageAvailableCount > 0
        ? photosToPlaceCount / minPageAvailableCount
        : densities[densities.length - 1];
    if (density <= densities[0]) {
      densityOnMinPage = densities[0];
    } else if (minPageAvailableCount > 0 && density <= densities[1]) {
      densityOnMinPage = densities[1];
    } else {
      densityOnMinPage = densities[1];
      const remainingPhotosCount = photosToPlaceCount - minPageAvailableCount * densities[1];

      if (remainingPhotosCount > 0) {
        if (remainingPhotosCount / maxAdditionalPageCount <= densities[0]) {
          additionnalPagesCount = Math.ceil(remainingPhotosCount / densities[0]);
          densityOnAdditionnalPages = densities[0];
        } else {
          additionnalPagesCount = Math.ceil(remainingPhotosCount / densities[1]);
          densityOnAdditionnalPages = densities[1];
        }
      }
    }

    return {
      minPagesToHave: minPageCount,
      densityForMinPages: densityOnMinPage,
      additionnalPagesCount,
      maxDensityOnAdditionnalPages: densityOnAdditionnalPages,
    };
  }
}

function getNewPageDefinition(
  layoutsForPage: TemplatePageDefinitionsByCountKey | null,
  density: number
) {
  const layoutsForPageKeys = getKeysByOrderAsc(layoutsForPage) || [];
  const goodDensityOfLayout = layoutsForPageKeys.find((nbEditablePictures) => {
    return +nbEditablePictures >= density;
  });
  const newPageDefinition =
    goodDensityOfLayout && layoutsForPage && layoutsForPage[+goodDensityOfLayout]
      ? layoutsForPage[+goodDensityOfLayout][0]
      : null;
  return newPageDefinition;
}

function addRequiredPages(
  requiredPagesCount: number,
  density: number,
  pageList: ContentPage[],
  defaultPageDefinitionKey: string,
  layoutsByPageDefinitionKey: LayoutsByCountKey,
  draftState: Required<InitialCustomizationState>,
  configuration: Configuration | undefined
) {
  const productOptions = configuration?.options || [];
  const defaultPageDefinition = findPageDefinition(defaultPageDefinitionKey, draftState.template);
  const effectiveIncrement = getNumberPagesToAdd(
    draftState.template,
    defaultPageDefinition,
    configuration
  );

  for (let index = 0; index < requiredPagesCount; index++) {
    const currentPage = pageList[index];
    if (!currentPage) {
      const newPageDefinition = getNewPageDefinition(
        layoutsByPageDefinitionKey[defaultPageDefinitionKey],
        density
      );
      if (newPageDefinition && index % effectiveIncrement === 0) {
        doAddPage(draftState, {
          configuration,
          pageDefinitionKey: newPageDefinition?.key,
          type: "addPage",
        });
      }
      continue;
    }

    const pageDefinition = findPageDefinition(currentPage.pageDefinitionKey, draftState.template);
    const currentPageDensity = countEditablePicturesInPage(pageDefinition);

    if (isEmptyPage(currentPage) && currentPageDensity !== density) {
      const newPageDefinition = getNewPageDefinition(
        layoutsByPageDefinitionKey[currentPage.pageDefinitionKey],
        density
      );
      if (newPageDefinition) {
        changeOnePagePageDefinition(currentPage, draftState, newPageDefinition.key, productOptions);
      }
    } else {
    }
  }
}
